import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useDataProvider } from "ra-core";

import type { CrmDataProvider } from "../providers/types";
import {
  useConfigurationUpdater,
  type ConfigurationContextValue,
} from "./ConfigurationContext";

export const useConfigurationLoader = () => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const updateConfiguration = useConfigurationUpdater();

  const { data } = useQuery<ConfigurationContextValue>({
    queryKey: ["configuration"],
    queryFn: () => dataProvider.getConfiguration(),
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: false,
  });

  useEffect(() => {
    if (!data || Object.keys(data).length === 0) return;

    let cancelled = false;
    const apply = async () => {
      let nextConfig = data;

      // Company sectors are sourced from Business Central (bc_segments).
      // If this fails (e.g. FakeRest/demo without bc_segments), we fall back to the stored config.
      try {
        const { data: bcSegments } = await dataProvider.getList("bc_segments", {
          pagination: { page: 1, perPage: 1000 },
          sort: { field: "number", order: "ASC" },
          filter: {},
        } as any);

        if (bcSegments?.length) {
          nextConfig = {
            ...data,
            companySectors: bcSegments.map((s: any) => ({
              value: String(s.number),
              label: s.description ?? s.number,
            })),
          };
        }
      } catch (e) {
        // Intentionally silent: sectors fallback to stored config
      }

      if (!cancelled) updateConfiguration(nextConfig);
    };

    apply();
    return () => {
      cancelled = true;
    };
  }, [data, dataProvider, updateConfiguration]);
};
